import { differenceInHours, isFuture, isToday, subDays } from 'date-fns';
import { sum } from 'lodash';
import { useMemo } from 'react';
import { AuthUserType } from '../auth/types';
import { useAuthContext } from '../auth/useAuthContext';
import { brandConfig } from '../config';
import { BrandConfig } from '../config/brandConfig';
import { useGetReservationRevenueQuery } from '../redux/rtkQuery/apiSlice';
import { ReservationGeneral, ReservationType } from './ReservationGeneral';
import { RevenueGeneral } from './RevenueGeneral';
import { ROIGeneral, ROISubType, ROIType } from './ROIGeneral';

const _emptyReservationUIObject = {
  Display_Travel_Insurance_Pitch: false,
  Display_Extend_Stay_Button: false,
  Disable_Extend_Stay_Button: false,
  Display_Request_Late_Check_Out_Button: false,
  Display_Payment_Info: false,
  Display_View_Invoice: false,
  Display_Update_Payment_Method: false,
  Display_View_Rental_Agreement: false,
  Add_Service: false,
  Edit_Flight_Info: false,
  Display_Add_Check_In_Request_Linked: false,
  Display_Add_Check_In_Request: false,
  Disable_Add_Check_In_Request: false,
  Display_Add_Guests: false,
  Allow_Update_Guest: false,
  Do_Not_Display_Support_Section: false,
  Display_Access_Codes: false,
  Display_Balance_Due_Alert: false,
  Display_Balance_Due_Warning: false,
  Display_Financials: false,
  Balance_Due_On_Checkout: false,
  Display_Pay_By_Bank_Banner: false,
  Display_Expired_Offer_Warning: false,
  Channel_Revenue_Restricted: false,
  Display_Add_Pets: false,
  Service_Only_Restricted: false,
};

export const buildReservationUIObject = (
  user?: AuthUserType,
  reservation?: ReservationGeneral,
  pendingPaymentAmount?: number,
  brand?: BrandConfig,
  rois?: ROIGeneral[],
  revenueData?: RevenueGeneral[]
) => {
  if (!reservation || typeof pendingPaymentAmount !== 'number') {
    return _emptyReservationUIObject;
  }
  const {
    extend_stay_allowed,
    external_revenue,
    has_late_check_out,
    has_travel_insurance,
    is_current_booking,
    is_future_booking,
    is_host,
    is_hybrid_child,
    is_hybrid_parent,
    is_inherited,
    is_past_booking,
    is_timeshare,
    late_check_out_allowed,
    late_check_out_amount,
    is_service_only,
    travel_insurance_eligible,
    is_balance_past_due,
    balance_due,
    hide_financials,
    check_in_ISO,
    check_out_ISO,
    is_guest_of_record,
    rental_agreement_id,
    travel_insurance_deadline_ISO,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payment_amount,
  } = reservation;

  const guestAssuranceROI = rois?.find(
    (roi) => roi.type === ROIType.MISC && roi.sub_type === ROISubType.GUEST_ASSURANCE_FEE
  );
  const hasGuestAssuranceROI = guestAssuranceROI && guestAssuranceROI?.total_amount > 0;

  const cardAuthorizationRevenue = revenueData?.find(
    (revenue) =>
      revenue.type === 'Authorization' &&
      ['Credit Card', 'Debit Card'].includes(revenue.tender) &&
      revenue.status !== 'Canceled'
  );
  const hasCardAuthorizationRevenue = cardAuthorizationRevenue !== undefined;

  const isVRBOBooking = reservation.booking_channel === 'VRBO';

  // Owner reservations do not show financials, payment info, or balance-due/overdue banners.
  const isOwnerReservation = reservation.type === ReservationType.Owner;
  const isGuestOfOwnerReservation = reservation.type === ReservationType.GuestOfOwner;

  // Calculate ACH payment deadline: check-in date minus 10 days (backend BANK_ACCOUNT_LEAD_DAYS = 10)
  // The banner should only show if we haven't passed this deadline
  const achPaymentDeadline = check_in_ISO ? subDays(check_in_ISO, 10) : null;
  const isBeforeACHDeadline = achPaymentDeadline
    ? isFuture(achPaymentDeadline) || isToday(achPaymentDeadline)
    : false;

  const balance_due_on_checkout = isToday(check_out_ISO);
  //--------- Display_Travel_Insurance_Pitch ---------//
  const Display_Travel_Insurance_Pitch =
    brandConfig.travelInsuranceEnabled &&
    is_host &&
    !is_hybrid_child &&
    !has_travel_insurance &&
    travel_insurance_eligible &&
    travel_insurance_deadline_ISO &&
    isFuture(travel_insurance_deadline_ISO) &&
    !is_inherited &&
    !isGuestOfOwnerReservation &&
    !external_revenue &&
    !is_service_only;

  // Note: Location-based TI logic is now handled in the TI banner component itself

  //-------------------------------------//

  return {
    Display_Travel_Insurance_Pitch,
    Display_Extend_Stay_Button: is_host && !is_past_booking && !is_hybrid_parent && !is_timeshare,
    Disable_Extend_Stay_Button: !extend_stay_allowed,
    Display_Request_Late_Check_Out_Button:
      is_host && // must be host
      !is_past_booking && // must not be past booking
      !is_timeshare && // must not be timeshare
      !is_hybrid_parent && // must not be hybrid parent
      !!late_check_out_amount && // must have late check out amount
      !has_late_check_out && // must not have late check out
      late_check_out_allowed && // must have late check out allowed
      differenceInHours(check_out_ISO, new Date()) < 48 && // must be less than 48 hours from checkout
      !is_service_only, // must not be a service only listing
    Display_Payment_Info:
      is_host && !is_hybrid_child && !external_revenue && !isOwnerReservation && !is_service_only,
    Display_View_Invoice: !external_revenue && !isOwnerReservation && !is_service_only,
    Add_Service: is_host && !is_past_booking && !is_timeshare,
    Edit_Flight_Info: is_host,
    Display_Add_Check_In_Request_Linked:
      is_host &&
      is_future_booking &&
      !is_timeshare &&
      is_hybrid_parent &&
      brand?.allowCheckInRequests &&
      !is_service_only,
    Display_Add_Check_In_Request:
      is_host &&
      is_future_booking &&
      !is_timeshare &&
      !is_hybrid_parent &&
      brand?.allowCheckInRequests &&
      !is_service_only,
    Disable_Add_Check_In_Request: !brand?.allowCheckInRequests || false,
    Display_Add_Guests: is_host && !is_past_booking,
    Allow_Update_Guest: is_host && !is_past_booking,
    Do_Not_Display_Support_Section: is_past_booking,
    Display_Access_Codes:
      is_current_booking && !is_timeshare && !(!external_revenue && balance_due > 0),
    Display_Balance_Due_Alert:
      !isOwnerReservation &&
      !isGuestOfOwnerReservation &&
      !is_service_only &&
      ((is_balance_past_due &&
        is_host &&
        !external_revenue &&
        balance_due - pendingPaymentAmount > 0) ||
      (balance_due_on_checkout &&
        is_host &&
        !external_revenue &&
        balance_due - pendingPaymentAmount > 0)),
    Display_Balance_Due_Warning:
      !isOwnerReservation &&
      !isGuestOfOwnerReservation &&
      !is_service_only &&
      balance_due > 0 &&
      !is_balance_past_due &&
      is_host &&
      !external_revenue &&
      balance_due - pendingPaymentAmount > 0,
    Display_Financials:
      !hide_financials && !isOwnerReservation && !external_revenue && !is_service_only,
    Display_Pay_By_Bank_Banner:
      !isOwnerReservation &&
      !is_service_only &&
      isVRBOBooking &&
      hasGuestAssuranceROI &&
      hasCardAuthorizationRevenue &&
      isBeforeACHDeadline,
    Display_Update_Payment_Method: is_guest_of_record && !is_service_only,
    Display_View_Rental_Agreement: is_guest_of_record && !!rental_agreement_id,
    Display_Expired_Offer_Warning:
      !isOwnerReservation &&
      !is_service_only &&
      isVRBOBooking &&
      hasGuestAssuranceROI &&
      !hasCardAuthorizationRevenue,
    Channel_Revenue_Restricted: external_revenue,
    Display_Add_Pets: reservation.max_pets > 0 && !is_service_only,
    Service_Only_Restricted: is_service_only,
  };
};

export default function useReservationUIConfig(
  reservation?: ReservationGeneral,
  rois?: ROIGeneral[]
) {
  const { user } = useAuthContext();
  const shouldSkipRevenueQuery =
    !reservation || !reservation.is_host || reservation.type === ReservationType.Owner;
  const {
    data: revenueData,
    isSuccess: revenueSuccess,
    isFetching: revenueFetching,
    // isError: revenueError,
    // error: revenueErrorObject
  } = useGetReservationRevenueQuery(
    {
      reservationName: reservation?.name,
    },
    { skip: shouldSkipRevenueQuery }
  );

  const pendingPayments = !revenueSuccess
    ? []
    : revenueData.filter((payment) => payment.status === 'Pending');
  const pendingPaymentAmount = sum(pendingPayments.map((payment) => payment.amount));

  const uiData = useMemo(
    () =>
      buildReservationUIObject(
        user,
        reservation,
        pendingPaymentAmount,
        brandConfig,
        rois,
        revenueData
      ),
    [reservation, pendingPaymentAmount, rois, revenueData, user]
  );
  if (revenueFetching) {
    return _emptyReservationUIObject;
  }
  return uiData;
}
